// Phase 12 — personal symbol library (client-only, no auth).
//
// One versioned localStorage record holds three lists:
//   • favorites      — explicit star/unstar set (ordered most-recent-first)
//   • recentlyViewed — symbol detail pages opened (max RECENT_LIMIT)
//   • recentlyCopied — every successful copy that flows through `copyText`
//
// Design notes:
//   • One key (`coolsymbols.library.v1`) so we can ship a single migration if
//     the shape changes — the version lives both in the key suffix *and* the
//     `version` field on disk for belt-and-braces detection.
//   • Degrades cleanly when localStorage is unavailable (SSR, private mode,
//     quota errors): mutations no-op against an in-memory store, reads still
//     return a consistent empty snapshot.
//   • External store + useSyncExternalStore so every panel and button
//     re-renders the instant any list changes.

import { useSyncExternalStore } from "react";

export const LIBRARY_SCHEMA_VERSION = 2 as const;
export const LIBRARY_STORAGE_KEY = `coolsymbols.library.v${LIBRARY_SCHEMA_VERSION}`;
/** Older keys we migrate from. Keep ordered oldest → newest. */
const LEGACY_STORAGE_KEYS: readonly string[] = ["coolsymbols.library.v1"];

export const FAVORITES_LIMIT = 256;
export const RECENT_LIMIT = 48;
export const SNIPPETS_LIMIT = 200;

export interface RecentEntry {
  /** The raw symbol string. */
  symbol: string;
  /** Unix ms timestamp of the event. */
  at: number;
}

export interface LibraryState {
  version: typeof LIBRARY_SCHEMA_VERSION;
  favorites: string[];
  /** Saved multi-character snippets (e.g. use-case example lines). */
  savedSnippets: string[];
  recentlyViewed: RecentEntry[];
  recentlyCopied: RecentEntry[];
}

const EMPTY_STATE: LibraryState = {
  version: LIBRARY_SCHEMA_VERSION,
  favorites: [],
  savedSnippets: [],
  recentlyViewed: [],
  recentlyCopied: [],
};

let state: LibraryState = EMPTY_STATE;
let hydrated = false;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function storageAvailable(): boolean {
  try {
    return typeof window !== "undefined" && !!window.localStorage;
  } catch {
    return false;
  }
}

function persist() {
  if (!storageAvailable()) return;
  try {
    window.localStorage.setItem(LIBRARY_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* quota / private mode — silently degrade to in-memory only */
  }
}

/**
 * Forward-compatible migration helper. Today we only have schema v1, but the
 * function exists so future versions can promote older payloads in-place.
 */
function migrate(raw: unknown): LibraryState | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;
  // v1 — current shape.
  if (obj.version === LIBRARY_SCHEMA_VERSION) {
    return sanitize(obj);
  }
  // Future versions land here, e.g.:
  //   if (obj.version === 1 && LIBRARY_SCHEMA_VERSION === 2) return upgradeV1toV2(obj);
  // For now anything else we treat as best-effort sanitize.
  return sanitize(obj);
}

// A storable symbol is a short Unicode string — at most a handful of
// codepoints (the longest real catalogue symbol is a 4-codepoint ZWJ sequence).
// This rejects share links, generator output, copy-all blobs and any other
// non-symbol value that should never appear in favorites/recents, and also
// scrubs such values out of previously-corrupted localStorage on load.
const MAX_SYMBOL_CODEPOINTS = 16;
function isStorableSymbol(s: unknown): s is string {
  if (typeof s !== "string" || s.length === 0) return false;
  if (s.length > 32) return false; // cheap UTF-16 guard before counting
  if (/\s/.test(s)) return false; // no whitespace in a symbol
  return [...s].length <= MAX_SYMBOL_CODEPOINTS;
}

// A storable snippet is a short multi-character line (a use-case example like
// "★★★★☆ (4/5)" or a two-line checklist). Spaces and newlines are allowed —
// that's the whole point — but absurdly long blobs and control chars are not.
const MAX_SNIPPET_LEN = 160;
function isStorableSnippet(s: unknown): s is string {
  if (typeof s !== "string") return false;
  if (s.trim().length === 0 || s.length > MAX_SNIPPET_LEN) return false;
  for (const ch of s) {
    const c = ch.codePointAt(0) ?? 0;
    // allow tab (9), newline (10), carriage return (13); reject other control chars
    if (c < 32 && c !== 9 && c !== 10 && c !== 13) return false;
  }
  return true;
}

function sanitize(obj: Record<string, unknown>): LibraryState {
  const favorites = Array.isArray(obj.favorites)
    ? obj.favorites.filter(isStorableSymbol).slice(0, FAVORITES_LIMIT)
    : [];
  const savedSnippets = Array.isArray(obj.savedSnippets)
    ? (obj.savedSnippets as unknown[]).filter(isStorableSnippet).slice(0, SNIPPETS_LIMIT)
    : [];
  const cleanRecent = (input: unknown): RecentEntry[] => {
    if (!Array.isArray(input)) return [];
    const out: RecentEntry[] = [];
    for (const item of input) {
      if (!item || typeof item !== "object") continue;
      const sym = (item as { symbol?: unknown }).symbol;
      const at = (item as { at?: unknown }).at;
      if (isStorableSymbol(sym) && typeof at === "number" && Number.isFinite(at)) {
        out.push({ symbol: sym, at });
      }
    }
    return out.slice(0, RECENT_LIMIT);
  };
  return {
    version: LIBRARY_SCHEMA_VERSION,
    favorites,
    savedSnippets,
    recentlyViewed: cleanRecent(obj.recentlyViewed),
    recentlyCopied: cleanRecent(obj.recentlyCopied),
  };
}

function hydrate() {
  if (hydrated || !storageAvailable()) return;
  hydrated = true;
  try {
    const raw = window.localStorage.getItem(LIBRARY_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const migrated = migrate(parsed);
      if (migrated) {
        state = migrated;
        emit();
        return;
      }
    }
    // Try legacy keys (none today; future-proofs schema bumps).
    for (const key of LEGACY_STORAGE_KEYS) {
      const legacy = window.localStorage.getItem(key);
      if (!legacy) continue;
      try {
        const migrated = migrate(JSON.parse(legacy));
        if (migrated) {
          state = migrated;
          persist();
          window.localStorage.removeItem(key);
          emit();
          return;
        }
      } catch {
        /* ignore corrupted legacy */
      }
    }
  } catch {
    /* corrupted record — keep empty state */
  }
}

function subscribe(fn: () => void): () => void {
  hydrate();
  listeners.add(fn);
  const onStorage = (e: StorageEvent) => {
    if (e.key !== LIBRARY_STORAGE_KEY) return;
    hydrated = false;
    hydrate();
  };
  if (storageAvailable()) {
    window.addEventListener("storage", onStorage);
  }
  return () => {
    listeners.delete(fn);
    if (storageAvailable()) {
      window.removeEventListener("storage", onStorage);
    }
  };
}

function getSnapshot(): LibraryState {
  return state;
}
function getServerSnapshot(): LibraryState {
  return EMPTY_STATE;
}

/** React hook — re-renders on every library mutation. */
export function useLibrary(): LibraryState {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

// ------------------- mutators -----------------------------------------------

function pushRecent(list: RecentEntry[], symbol: string, at: number): RecentEntry[] {
  const filtered = list.filter((e) => e.symbol !== symbol);
  filtered.unshift({ symbol, at });
  return filtered.slice(0, RECENT_LIMIT);
}

export function isFavorite(symbol: string): boolean {
  hydrate();
  return state.favorites.includes(symbol);
}

export function toggleFavorite(symbol: string): boolean {
  hydrate();
  if (!isStorableSymbol(symbol)) return false;
  const already = state.favorites.includes(symbol);
  let nextFavs: string[];
  if (already) {
    nextFavs = state.favorites.filter((s) => s !== symbol);
  } else {
    nextFavs = [symbol, ...state.favorites].slice(0, FAVORITES_LIMIT);
  }
  state = { ...state, favorites: nextFavs };
  persist();
  emit();
  return !already;
}

export function recordView(symbol: string): void {
  hydrate();
  if (!isStorableSymbol(symbol)) return;
  state = { ...state, recentlyViewed: pushRecent(state.recentlyViewed, symbol, Date.now()) };
  persist();
  emit();
}

export function recordCopy(symbol: string): void {
  hydrate();
  if (!isStorableSymbol(symbol)) return;
  state = { ...state, recentlyCopied: pushRecent(state.recentlyCopied, symbol, Date.now()) };
  persist();
  emit();
}

export function clearFavorites(): void {
  hydrate();
  if (state.favorites.length === 0) return;
  state = { ...state, favorites: [] };
  persist();
  emit();
}

export function isSnippetSaved(text: string): boolean {
  hydrate();
  return state.savedSnippets.includes(text);
}

export function toggleSnippet(text: string): boolean {
  hydrate();
  if (!isStorableSnippet(text)) return false;
  const already = state.savedSnippets.includes(text);
  const next = already
    ? state.savedSnippets.filter((s) => s !== text)
    : [text, ...state.savedSnippets].slice(0, SNIPPETS_LIMIT);
  state = { ...state, savedSnippets: next };
  persist();
  emit();
  return !already;
}

export function clearSnippets(): void {
  hydrate();
  if (state.savedSnippets.length === 0) return;
  state = { ...state, savedSnippets: [] };
  persist();
  emit();
}

export function clearRecentlyViewed(): void {
  hydrate();
  if (state.recentlyViewed.length === 0) return;
  state = { ...state, recentlyViewed: [] };
  persist();
  emit();
}

export function clearRecentlyCopied(): void {
  hydrate();
  if (state.recentlyCopied.length === 0) return;
  state = { ...state, recentlyCopied: [] };
  persist();
  emit();
}

export function clearLibrary(): void {
  hydrate();
  state = { ...EMPTY_STATE };
  if (storageAvailable()) {
    try {
      window.localStorage.removeItem(LIBRARY_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }
  emit();
}
